'use client';

import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { DOC_SECTIONS } from '@/app/[tenant]/mcp/docs/sections';
import Logo from '@/components/bazak-logo';
import { useModal } from '@/components/modals/modal-provider';
import { Button } from '@/components/ui/button';

import { ThemeToggleButton } from './theme-toggle-button';

// In homepage scroll order. `id` is the in-page anchor (smooth-scrolled on the
// homepage); `href` is the fallback used when navigating from another route.
const NAV_LINKS = [
  { label: 'The Opportunity', href: '/#opportunity', id: 'opportunity' },
  { label: 'Trusted by', href: '/#trusted-by', id: 'trusted-by' },
  { label: 'In the News', href: '/#news', id: 'news' },
  { label: 'Features', href: '/#features', id: 'features' },
  { label: 'Pricing', href: '/pricing', id: 'pricing' },
];

export function Header() {
  const pathname = usePathname();
  const isConnectorDocs = /\/mcp\/docs\/?$/.test(pathname);
  const prefersReducedMotion = useReducedMotion();
  const { openModal } = useModal();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  // Focus management for mobile menu
  useEffect(() => {
    if (isOpen) {
      const firstFocusable = menuRef.current?.querySelector<HTMLElement>(
        'a, button, input, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      menuButtonRef.current?.focus();
      wasOpenRef.current = false;
    }
  }, [isOpen]);

  // Close menu on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);

  // Handle smooth scroll and close menu
  const handleNavClick = (e: MouseEvent<HTMLAnchorElement>, elementId: string | null) => {
    // Close first so the mobile menu also closes on off-home links, which fall
    // through to the href navigation below.
    closeMenu();
    if (pathname !== '/') return;
    e.preventDefault();

    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        const top = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <header className="fixed top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
        <div className="mx-auto flex h-14 max-w-6xl items-center px-4">
          <div className="mr-4">
            <Link
              href="/"
              className="mr-6 flex items-center space-x-2"
              onClick={e => handleNavClick(e, null)}
            >
              <Logo className="h-7 w-auto" />
            </Link>
          </div>
          {/* Connector docs are submitted to AI directories, not part of the marketing funnel, so
              the site nav and contact CTA give way to the page's own sections. */}
          {isConnectorDocs ? (
            <>
              <nav
                aria-label="On this page"
                className="hidden items-center space-x-6 min-[720px]:flex"
              >
                {Object.values(DOC_SECTIONS).map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-sm font-medium underline-offset-4 transition-colors hover:text-primary hover:underline"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
              <div className="flex flex-1 items-center justify-end">
                <ThemeToggleButton />
              </div>
            </>
          ) : (
            <>
              {/* Desktop Navigation */}
              <nav
                aria-label="Main navigation"
                className="hidden items-center space-x-6 min-[900px]:flex"
              >
                {NAV_LINKS.map(link => (
                  <Link
                    key={link.id}
                    href={link.href}
                    onClick={e => handleNavClick(e, link.id)}
                    className="text-sm font-medium underline-offset-4 transition-colors hover:text-primary hover:underline"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              <div className="hidden flex-1 items-center justify-end space-x-2 min-[900px]:flex">
                <Button size="default" onClick={() => openModal('contact', 'header')}>
                  Contact Us
                </Button>
                <ThemeToggleButton />
              </div>

              {/* Mobile Navigation */}
              <div className="flex flex-1 items-center justify-end min-[900px]:hidden">
                <Button
                  ref={menuButtonRef}
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(prev => !prev)}
                  aria-label={isOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={isOpen}
                  aria-controls="mobile-menu"
                  className="relative"
                >
                  <Menu
                    aria-hidden="true"
                    className={`absolute h-5 w-5 transition-all duration-350 ${
                      isOpen ? 'scale-0 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100'
                    }`}
                  />
                  <X
                    aria-hidden="true"
                    className={`absolute h-5 w-5 transition-all duration-350 ${
                      isOpen ? 'scale-100 rotate-0 opacity-100' : 'scale-0 -rotate-90 opacity-0'
                    }`}
                  />
                </Button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence mode="wait">
        {isOpen && !isConnectorDocs && (
          <motion.div
            key="mobile-menu"
            initial={prefersReducedMotion ? false : { opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -16 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-40 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 min-[900px]:hidden"
            style={{ top: '3.5rem' }}
          >
            <nav
              ref={menuRef}
              id="mobile-menu"
              aria-label="Mobile navigation"
              className="flex h-full flex-col px-6 py-6 pb-8"
            >
              <div className="flex-1 space-y-6">
                {NAV_LINKS.map(link => (
                  <Link
                    key={link.id}
                    href={link.href}
                    onClick={e => handleNavClick(e, link.id)}
                    className="block text-lg font-medium transition-colors hover:text-primary"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="flex items-center gap-3 border-t pt-6">
                <Button
                  size="default"
                  className="flex-1"
                  onClick={() => {
                    closeMenu();
                    openModal('contact', 'header_mobile');
                  }}
                >
                  Contact Us
                </Button>
                <ThemeToggleButton />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
